/**
 * THE FIRST FIVE MINUTES, AS A SEQUENCE RATHER THAN A SCREEN.
 *
 * WHAT WAS THERE BEFORE THIS. A single card saying "no boards here yet" with a
 * button on it. Better than the placeholder board it replaced, and still not an
 * onboarding: it named one of the three things a new customer has to do and
 * left them to find the other two. Somebody who made a board and then wondered
 * where their team was had been told nothing wrong — just not enough.
 *
 * THREE STEPS, IN THIS ORDER, AND THE ORDER IS THE DESIGN.
 *
 *   1. You        — your name, before anything you write is signed with an
 *                   email address. It is also the cheapest possible first
 *                   step: everybody can answer it, nobody can get it wrong,
 *                   and finishing a step is what makes somebody finish the
 *                   next one.
 *   2. Your team  — before the board rather than after, because a board with
 *                   nobody on it is a board you have to go back and share.
 *                   Skippable in one press: plenty of people genuinely are on
 *                   their own, and asking them to prove it is rude.
 *   3. Your board — last, because it is the only step that cannot be undone
 *                   cheaply and the only one that needs a decision (its key
 *                   goes on every issue reference for ever).
 *
 * NOTHING HERE IS SKIPPABLE, AND THAT IS A CORRECTION.
 *
 * The first version let the first two steps be passed with "Skip for now", on
 * the reasoning that a setup which cannot be escaped is one people lie to.
 * That reasoning is right about BUSYWORK and wrong about this: these three
 * steps ARE the account, the team and the workspace. There is nothing here to
 * skip to — skipping the name gives you a workspace where everything you write
 * is signed with an email address, and skipping the team gives you a board
 * nobody else can see.
 *
 * WHAT SURVIVES OF THE OBJECTION is the team step, where "nobody yet" is a
 * genuine answer rather than an evasion — plenty of people really are on their
 * own. So it must be ANSWERED rather than passed: add somebody, or say it is
 * just you. Both are answers; neither is a skip.
 */
import { parseAddresses, type AddressList } from './people';
import { keyFromName, projectProblem, type Project } from './projects';
import type { ProjectRole } from './permissions';

export type StepId = 'you' | 'team' | 'board';

export interface Step {
  id: StepId;
  title: string;
  /** One line under the title. Says why, not what — the what is the fields. */
  blurb: string;
  /**
   * Whether "nobody yet" is one of the answers.
   *
   * NOT "can be skipped" — nothing here can. It is the difference between a
   * step with two valid answers and a step with one.
   */
  allowsNone: boolean;
}

export const STEPS: Step[] = [
  {
    id: 'you',
    title: 'Who you are',
    blurb: 'Your name goes on everything you write here, in place of your email address.',
    allowsNone: false,
  },
  {
    id: 'team',
    title: 'Who else is here',
    blurb:
      'Add them now and your first board is theirs too. You run it whatever you choose for them, and you can add more people later.',
    allowsNone: true,
  },
  {
    id: 'board',
    title: 'Your first board',
    blurb: 'One piece of work — a product, a project, a client — with its own issues and history.',
    allowsNone: false,
  },
];

export interface Draft {
  firstName: string;
  lastName: string;
  title: string;
  /** Whatever was pasted into the team box, unparsed. */
  team: string;
  teamRole: ProjectRole;
  boardName: string;
  boardKey: string;
  /** Whether the key has been typed into, so it stops following the name. */
  keyIsTheirs: boolean;
  /**
   * "It is just me for now" — and `null` until somebody says.
   *
   * THREE STATES, NOT TWO, and the third is the reason. With a plain boolean,
   * `false` is both "I will add my team" and "nobody has answered yet" — so
   * the screen showed that option already selected while the Next button sat
   * disabled, which reads as stuck rather than as a question. Null means
   * neither is chosen, so nothing is claimed on somebody's behalf.
   */
  solo: boolean | null;
}

export const EMPTY_DRAFT: Draft = {
  firstName: '',
  lastName: '',
  title: '',
  team: '',
  teamRole: 'reviewer',
  boardName: '',
  boardKey: '',
  keyIsTheirs: false,
  solo: null,
};

/** The key, which follows the board's name until somebody edits it. */
export function keyFor(draft: Draft): string {
  return draft.keyIsTheirs ? draft.boardKey : keyFromName(draft.boardName);
}

/**
 * WHAT STOPS THIS STEP BEING FINISHED — empty where nothing does.
 *
 * Only the board can fail, and it fails for the reasons a board always fails:
 * no name, no key, or a key already in use. The other two steps cannot be
 * wrong, which is why they are first: a sequence that refuses somebody on its
 * opening screen is one they abandon.
 */
export function stepProblem(step: StepId, draft: Draft, existing: Pick<Project, 'name' | 'key'>[]): string {
  if (step === 'you') {
    // A first name is the whole requirement. Asking for a surname as well
    // excludes everybody who does not use one.
    if (!draft.firstName.trim()) return 'What should we call you?';
    return '';
  }
  if (step === 'team') {
    if (draft.solo === true) return '';
    if (draft.solo === false && parseAddresses(draft.team).ready.length > 0) return '';
    if (draft.solo === null) return 'Say whether anybody else is working with you.';
    // Named rather than "this field is required", because the answer they are
    // missing is a choice between two things and one of them is a button.
    return 'Add somebody, or say it is just you for now.';
  }
  return projectProblem({ name: draft.boardName, key: keyFor(draft) }, existing);
}

/**
 * Whether this step has been answered — which now means the same as whether it
 * may be passed, because nothing here can be skipped.
 *
 * Kept as its own function because it is asked BEFORE the button is pressed,
 * to decide whether the button is live; stepProblem answers the same question
 * afterwards, in words.
 */
export function stepAnswered(step: StepId, draft: Draft): boolean {
  return stepProblem(step, draft, []) === '';
}

/** The people about to be added, so the step can say how many before it acts. */
export function teamFrom(draft: Draft, existing: string[]): AddressList {
  return parseAddresses(draft.team, existing);
}

/**
 * WHAT THE LAST STEP PROMISES, so pressing it is not a leap.
 *
 * Everything happens at the end rather than step by step, which is deliberate:
 * somebody who abandons this half way should leave nothing behind — no
 * half-named profile, no people invited to a workspace with no board in it.
 */
export function finishSummary(draft: Draft, addingCount: number): string {
  const bits = [`Make ${draft.boardName.trim() || 'the board'}`];
  if (addingCount) bits.push(`add ${addingCount} ${addingCount === 1 ? 'person' : 'people'}`);
  if (draft.firstName.trim()) bits.push('save your name');
  return bits.length === 1
    ? `${bits[0]} and open it.`
    : `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}.`;
}
